import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import DepartmentLevelForm from "Components/organisms/forms/department-level-form"
import DepartmentLevelFields from "Components/organisms/forms/department-level-form/types"
import { DepartmentLevelFormValidation } from "data/department-level-form"
import { DepartmentLevel } from "models/department-level"
import {
    AdminDepartmentLevelsSliceState,
    setDepartmentLevels,
    addDepartmentLevel,
    setDepartmentLevel,
    removeDepartmentLevel,
    setEditingDepartmentLevel
} from "slices/admin/department-levels/reducer"
import {
    getDepartmentLevels,
    submitCreateDepartmentLevelForm,
    submitEditDepartmentLevelForm,
    deleteDepartmentLevelRequest
} from "slices/admin/department-levels/thunk"
import { PlusIcon, PencilIcon, TrashIcon, HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"


const selectDepartmentLevels = createSelector(
    (state: any) => state.AdminDepartmentLevels,
    ({ departmentLevels, editingDepartmentLevel }: AdminDepartmentLevelsSliceState) => ({
        departmentLevels,
        editingDepartmentLevel
    })
)

const DepartmentLevelsPage = () => {
    document.title = "Department Levels | IbexIQ";
    const { handleOpenManual } = useManual("admin/department-levels");
    const dispatch: any = useDispatch()
    const { departmentLevels, editingDepartmentLevel } = useSelector(selectDepartmentLevels)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [showFormModal, setShowFormModal] = useState<boolean>(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [focusedDepartmentLevel, setFocusedDepartmentLevel] = useState<DepartmentLevel | null>(null)
    const [isDeleting, setIsDeleting] = useState<boolean>(false)

    useEffect(() => {
        const init = async () => {
            const res = await getDepartmentLevels()
            if (res) dispatch(setDepartmentLevels(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const handleCloseFormModal = () => {
        setShowFormModal(false)
        dispatch(setEditingDepartmentLevel(null))
        resetForm()
    }

    const onSubmit = async (vals: DepartmentLevelFields, helpers: FormikHelpers<DepartmentLevelFields>) => {
        helpers.setSubmitting(true)

        if (editingDepartmentLevel) {
            const res = await submitEditDepartmentLevelForm(editingDepartmentLevel.id, vals)
            if (res) {
                dispatch(setDepartmentLevel(res))
                handleCloseFormModal()
            }
        } else {
            const res = await submitCreateDepartmentLevelForm(vals)
            if (res) {
                dispatch(addDepartmentLevel(res))
                handleCloseFormModal()
            }
        }

        helpers.setSubmitting(false)
    }

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        resetForm,
        isSubmitting,
        isValid
    } = useFormik({
        initialValues: {
            description: '',
            depth: ''
        } as DepartmentLevelFields,
        onSubmit,
        validationSchema: DepartmentLevelFormValidation
    })

    const handleAddDepartmentLevel = () => {
        dispatch(setEditingDepartmentLevel(null))
        setValues({ description: '', depth: '' }, false)
        setShowFormModal(true)
    }

    const handleEditDepartmentLevel = (departmentLevel: DepartmentLevel) => {
        dispatch(setEditingDepartmentLevel(departmentLevel))
        setValues({ description: departmentLevel.description, depth: departmentLevel.depth }, false)
        setShowFormModal(true)
    }

    const handleDeleteDepartmentLevel = (departmentLevel: DepartmentLevel) => {
        setFocusedDepartmentLevel(departmentLevel)
        setIsDeleteModalOpen(true)
    }

    const handleCloseDeleteModal = async () => {
        setIsDeleteModalOpen(false)
        setFocusedDepartmentLevel(null)
    }

    const confirmDeleteDepartmentLevel = async () => {
        if (!focusedDepartmentLevel) return
        setIsDeleting(true)
        const success = await deleteDepartmentLevelRequest(focusedDepartmentLevel.id)
        if (success) {
            dispatch(removeDepartmentLevel(focusedDepartmentLevel.id))
            handleCloseDeleteModal()
        }
        setIsDeleting(false)
    }

    const columns = React.useMemo<ColumnDef<DepartmentLevel>[]>(() => [
        {
            header: "Depth",
            accessorKey: "depth",
            size: 80,
        },
        {
            header: "Description",
            accessorKey: "description",
            size: 200,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                {row.original.description}
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleEditDepartmentLevel(row.original)}><PencilIcon /></IconButton>
                    <IconButton onClick={() => handleDeleteDepartmentLevel(row.original)}><TrashIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        }
    ], [])

    const table = useDataTable({
        columns,
        data: departmentLevels,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Department Levels"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={<>
            <ActionButton onClick={handleAddDepartmentLevel}>Add <PlusIcon /></ActionButton>
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
        modal={<>
            <Modal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                title={editingDepartmentLevel ? "Edit Department Level" : "Create Department Level"}
            >
                <DepartmentLevelForm
                    edit={!!editingDepartmentLevel}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isValid={isValid}
                    isSubmitting={isSubmitting}
                />
            </Modal>
            <AreYouSureModal
                isOpen={isDeleteModalOpen}
                onModalClose={handleCloseDeleteModal}
                modalTitle="Delete Level"
                subTitle="Are you sure you want to delete the department level"
                handleAreYouSureAccept={confirmDeleteDepartmentLevel}
                handleAreYouSureDecline={handleCloseDeleteModal}
                areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
                areYouSuredeclineText="Cancel"
            />
        </>}
    />
}

export default DepartmentLevelsPage
